import React, { useState, useEffect } from 'react';
import {
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Divider,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import api from '../../../utils/api';
import { _date } from '../../../utils/helpers';

export default function ContactFormModal({ open, onClose, contactFormId, onUpdate }) {
    const [contactForm, setContactForm] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [status, setStatus] = useState('pending');
    const [saving, setSaving] = useState(false);
    const [statusError, setStatusError] = useState('');

    useEffect(() => {
        if (open && contactFormId) {
            fetchContactForm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, contactFormId]);

    const fetchContactForm = async () => {
        setLoading(true);
        setLoadError('');
        try {
            const response = await api.get(`admin/contact-form/${contactFormId}`);
            setContactForm(response.data);
            setStatus(response.data?.status || 'pending');
        } catch (error) {
            console.error('Failed to fetch contact form:', error);
            setLoadError(error.response?.data?.message || 'Failed to load contact form.');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (newStatus) => {
        const previousStatus = status;
        setStatus(newStatus);
        setStatusError('');
        setSaving(true);
        try {
            await api.put(`admin/contact-form/${contactFormId}`, { status: newStatus });
            if (contactForm) {
                setContactForm({ ...contactForm, status: newStatus });
            }
            if (onUpdate) {
                onUpdate();
            }
        } catch (error) {
            console.error('Failed to update status:', error);
            setStatus(previousStatus);
            setStatusError(error.response?.data?.message || 'Failed to update status.');
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        setContactForm(null);
        setStatus('pending');
        setStatusError('');
        setLoadError('');
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Contact Form Details</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {contactForm && (
                            <>
                                <Chip
                                    label={contactForm.type === 'free_course' ? 'Free Course' : 'Contact'}
                                    color={contactForm.type === 'free_course' ? 'warning' : 'primary'}
                                    size="small"
                                />
                                <Chip
                                    label={contactForm.red_at ? "Read" : "New"}
                                    color={contactForm.red_at ? "default" : "primary"}
                                    size="small"
                                />
                            </>
                        )}
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent>
                {loadError ? <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert> : null}
                {statusError ? <Alert severity="error" sx={{ mb: 2 }}>{statusError}</Alert> : null}
                {loading ? (
                    <Typography>Loading...</Typography>
                ) : contactForm ? (
                    <Box sx={{ mt: 2 }}>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                                Name
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {contactForm.name}
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                                Email
                            </Typography>
                            <Typography variant="body1">
                                {contactForm.email || '—'}
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                                Phone
                            </Typography>
                            <Typography variant="body1">
                                {contactForm.phone ? (
                                    <a href={`tel:${contactForm.phone}`} style={{ color: 'inherit' }}>
                                        {contactForm.phone}
                                    </a>
                                ) : (
                                    '—'
                                )}
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                                Message
                            </Typography>
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                {contactForm.message || '—'}
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                                Type
                            </Typography>
                            <Chip
                                label={contactForm.type === 'free_course' ? 'Free Course' : 'Contact'}
                                color={contactForm.type === 'free_course' ? 'warning' : 'primary'}
                                size="small"
                            />
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                                Status
                            </Typography>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={status}
                                    label="Status"
                                    onChange={(e) => handleStatusChange(e.target.value)}
                                    disabled={saving}
                                >
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="in_progress">In Progress</MenuItem>
                                    <MenuItem value="completed">Completed</MenuItem>
                                    <MenuItem value="cancelled">Cancelled</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                                Submitted Date
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {_date(contactForm.created_at)}
                            </Typography>
                        </Box>

                        {contactForm.red_at && (
                            <>
                                <Divider sx={{ my: 2 }} />
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                                        Read Date
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        {_date(contactForm.red_at)}
                                    </Typography>
                                </Box>
                            </>
                        )}
                    </Box>
                ) : (
                    <Typography>No data available</Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
